/**
 * PATH: packages/ui/src/kanban/components/card-detail/CardAttachmentsSection.tsx
 * FILE_ID: UI-KANBAN-CMP-CARD-ATTACHMENTS-001
 * PURPOSE: Real file upload / list / download / delete for a Kanban card,
 *          rendered in the drawer's Files tab. Net-new manual files (drag or
 *          pick) — distinct from the email-derived files CardFilesRail shows.
 * WHY:     Operator ask 2026-06-29: "build [card file upload/delete] in and
 *          tie it to qdrive properly". Before this, the Files tab could only
 *          list opaque email attachment ids — there was no way to attach an
 *          arbitrary file to a card. Each upload here becomes a REAL QDrive
 *          FileRecord server-side (source_type='kanban_card'), so the file is
 *          searchable / versioned / presigned-downloadable like any QDrive
 *          file, not a parallel store.
 * FLOW:
 *   ┌────────┐  multipart   ┌──────────────────────────┐  list   ┌────────┐
 *   │ pick / │ ───────────▶ │ api.uploadCardAttachment  │ ──────▶ │ rows + │
 *   │ drop   │              │ (→ QDrive FileRecord)     │         │ delete │
 *   └────────┘              └──────────────────────────┘         └────────┘
 * DEPENDENCIES:
 *   - api.{list,upload,delete}CardAttachment: OPTIONAL KanbanApi methods; the
 *     parent gates rendering on `uploadCardAttachment` so this only mounts when
 *     the host wired the BE surface.
 *   - components/email/attachmentClassify.formatFileSize: pure size formatter.
 *   - manualAttachmentDoc: ARC-2 MR7 — maps rows onto the in-card editor
 *     overlay + version rail (uploads ARE QDrive FileRecords).
 * RELATED:
 *   - CardFilesRail.tsx: the sibling that lists email-derived files.
 *   - CardDetailDrawer.tsx Files tab: mounts this above the email rail.
 */
import { useRef, useState, type DragEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Download,
  File as FileIcon,
  FileImage,
  FileText,
  FileType,
  History,
  Loader2,
  Trash2,
  Upload,
  type LucideIcon,
} from 'lucide-react'

import { cn } from '../../../lib/utils'
import { formatFileSize } from '../../../components/email/attachmentClassify'
import type { KanbanApi, KanbanEmailAdapter } from '../../api'
import type { Card, CardAttachment } from '../../types'
import { cardCommentsQueryKey } from '../peekQueryKeys'
import { FileVersionRail, fileVersionsQueryKey } from './FileVersionRail'
import { opensInDocEditor, toDocAttachment } from './manualAttachmentDoc'
import {
  isOversizedAttachment,
  oversizedAttachmentMessage,
  summarizeUploadFailures,
  type AttachmentUploadFailure,
} from './attachmentUploadLimits'

/** Shared react-query key segment so callers can invalidate the list. */
export const CARD_ATTACHMENTS_QUERY_SEG = 'card-attachments'

export function cardAttachmentsQueryKey(cardId: string) {
  return ['kanban', CARD_ATTACHMENTS_QUERY_SEG, cardId] as const
}

function pickIcon(contentType: string, filename: string): LucideIcon {
  const ct = (contentType || '').toLowerCase()
  const name = (filename || '').toLowerCase()
  if (ct.startsWith('image/')) return FileImage
  if (ct === 'application/pdf' || name.endsWith('.pdf')) return FileType
  if (ct.startsWith('text/')) return FileText
  return FileIcon
}

export interface CardAttachmentsSectionProps {
  card: Card
  api: KanbanApi
  /** ARC-2 MR7 — when the tenant wired the document layer, PDF/image rows
   *  open IN-CARD (editor overlay) and expose the version rail. Optional
   *  so older hosts keep the download-only behaviour. */
  emailAdapter?: KanbanEmailAdapter
}

export function CardAttachmentsSection({ card, api, emailAdapter }: CardAttachmentsSectionProps) {
  const qc = useQueryClient()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [openDoc, setOpenDoc] = useState<CardAttachment | null>(null)
  const [historyFor, setHistoryFor] = useState<string | null>(null)

  const canList = !!api.listCardAttachments
  const canUpload = !!api.uploadCardAttachment
  const canDelete = !!api.deleteCardAttachment

  const listQ = useQuery({
    queryKey: cardAttachmentsQueryKey(card.id),
    queryFn: () => api.listCardAttachments!(card.id),
    enabled: canList,
    staleTime: 15_000,
  })

  const invalidate = () =>
    void qc.invalidateQueries({ queryKey: cardAttachmentsQueryKey(card.id) })

  const uploadM = useMutation({
    mutationFn: (file: File) => api.uploadCardAttachment!(card.id, file),
    onSuccess: invalidate,
  })
  const deleteM = useMutation({
    mutationFn: (fileId: string) => api.deleteCardAttachment!(card.id, fileId),
    onSuccess: invalidate,
  })

  // BUG-2026-07-09-KANBAN-ATTACH-UPLOAD-NO-CLIENT-GUARDS — size-gate each
  // file BEFORE the mutation (the 50 MB cap used to live only server-side)
  // and accumulate per-file failures instead of overwriting the error, so
  // a mixed batch reports every failed filename while successes stay up.
  async function uploadFiles(files: FileList | File[]) {
    setError(null)
    const batch = Array.from(files)
    const failures: AttachmentUploadFailure[] = []
    for (const f of batch) {
      if (isOversizedAttachment(f.size)) {
        failures.push({ filename: f.name, message: oversizedAttachmentMessage(f.name, f.size) })
        continue
      }
      try {
        await uploadM.mutateAsync(f)
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Upload failed'
        failures.push({ filename: f.name, message: `${f.name}: ${msg}` })
      }
    }
    setError(summarizeUploadFailures(failures, batch.length))
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragOver(false)
    if (canUpload && e.dataTransfer?.files?.length) {
      void uploadFiles(e.dataTransfer.files)
    }
  }

  if (!canList && !canUpload) return null

  const files = listQ.data ?? []
  const uploading = uploadM.isPending

  return (
    <section aria-label="Uploaded files" className="space-y-2">
      <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-neutral-500 dark:text-neutral-400">
        Attachments{files.length ? ` · ${files.length}` : ''}
      </h3>

      {canUpload && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Upload a file to this card"
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click()
          }}
          onDragOver={(e) => {
            e.preventDefault()
            setDragOver(true)
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={cn(
            'flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-3 text-[11.5px] transition-colors',
            dragOver
              ? 'border-blue-400 bg-blue-50/60 text-blue-700 dark:border-blue-500/60 dark:bg-blue-950/30 dark:text-blue-300'
              : 'border-neutral-300 text-neutral-500 hover:border-blue-400 hover:bg-blue-50/30 dark:border-zinc-700 dark:text-zinc-400 dark:hover:border-blue-500/50',
          )}
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin text-blue-600" aria-hidden />
          ) : (
            <Upload className="h-4 w-4" aria-hidden />
          )}
          <span>
            {uploading ? 'Uploading…' : 'Drag a file here, or click to upload'}
          </span>
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) void uploadFiles(e.target.files)
              e.target.value = ''
            }}
          />
        </div>
      )}

      {error && (
        <p className="text-[11px] text-rose-600 dark:text-rose-400">{error}</p>
      )}

      {listQ.isLoading ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div
              key={i}
              className="h-[42px] animate-pulse rounded-lg bg-neutral-100 dark:bg-zinc-800"
            />
          ))}
        </div>
      ) : files.length === 0 ? (
        <p className="px-1 py-1 text-[11.5px] text-neutral-400 dark:text-zinc-500">
          No files uploaded to this card yet.
        </p>
      ) : (
        <ul role="list" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {files.map((f) => {
            const openable = opensInDocEditor(f, emailAdapter)
            const showHistory = openable && !!emailAdapter?.listFileVersions
            const historyOpen = historyFor === f.id
            return (
              <li key={f.id} className="min-w-0 space-y-1">
                <AttachmentRow
                  file={f}
                  canDelete={canDelete}
                  deleting={deleteM.isPending && deleteM.variables === f.id}
                  onDelete={() => deleteM.mutate(f.id)}
                  onOpen={openable ? () => setOpenDoc(f) : undefined}
                  onToggleHistory={
                    showHistory
                      ? () => setHistoryFor(historyOpen ? null : f.id)
                      : undefined
                  }
                  historyOpen={historyOpen}
                />
                {historyOpen && emailAdapter && (
                  <FileVersionRail fileId={f.id} emailAdapter={emailAdapter} />
                )}
              </li>
            )
          })}
        </ul>
      )}

      {/* ARC-2 MR7 — the host overlay portal (same seam CardFilesRail uses).
          A markup save creates a QDrive version AND may change the live
          summary (size/version) → refresh both the version rail and the
          attachment list. */}
      {openDoc && emailAdapter?.DocumentEditorOverlay && (
        <emailAdapter.DocumentEditorOverlay
          attachment={toDocAttachment(openDoc)}
          emailId={null}
          cardId={card.id}
          onClose={() => setOpenDoc(null)}
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: fileVersionsQueryKey(openDoc.id) })
            invalidate()
          }}
          onFollowUpCreated={() => {
            void qc.invalidateQueries({ queryKey: cardCommentsQueryKey(card.id) })
            invalidate()
          }}
        />
      )}
    </section>
  )
}

function AttachmentRow({
  file,
  canDelete,
  deleting,
  onDelete,
  onOpen,
  onToggleHistory,
  historyOpen,
}: {
  file: CardAttachment
  canDelete: boolean
  deleting: boolean
  onDelete: () => void
  /** ARC-2 MR7 — present for PDF/image rows when the host injected the
   *  document overlay: primary click opens IN-CARD instead of nothing. */
  onOpen?: () => void
  onToggleHistory?: () => void
  historyOpen?: boolean
}) {
  const Icon = pickIcon(file.content_type, file.filename)
  const Body = (
    <>
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-500/10">
        <Icon className="h-3.5 w-3.5 text-slate-600 dark:text-slate-300" aria-hidden />
      </span>
      <span className="flex min-w-0 flex-1 flex-col text-left">
        <span className="truncate text-[11.5px] font-medium text-neutral-700 dark:text-neutral-200">
          {file.filename}
        </span>
        <span className="truncate text-[10px] text-neutral-400 dark:text-neutral-500">
          {file.file_size ? formatFileSize(file.file_size) : ''}
          {file.uploaded_by_name ? ` · ${file.uploaded_by_name}` : ''}
        </span>
      </span>
    </>
  )
  return (
    <div className="flex items-center gap-2 rounded-lg border border-neutral-200 px-2.5 py-1.5 dark:border-zinc-800">
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          title={`Open ${file.filename} in the editor`}
          className="flex min-w-0 flex-1 items-center gap-2 rounded-md text-left transition-colors hover:bg-neutral-50 dark:hover:bg-zinc-800/60"
        >
          {Body}
        </button>
      ) : (
        <span className="flex min-w-0 flex-1 items-center gap-2">{Body}</span>
      )}
      {onToggleHistory && (
        <button
          type="button"
          onClick={onToggleHistory}
          title={historyOpen ? 'Hide version history' : 'Show version history'}
          aria-expanded={historyOpen}
          className={cn(
            'shrink-0 rounded p-1 transition-colors hover:bg-neutral-100 dark:hover:bg-zinc-800',
            historyOpen
              ? 'text-blue-600 dark:text-blue-400'
              : 'text-neutral-400 dark:text-zinc-500',
          )}
        >
          <History className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
      {file.download_url && (
        <a
          href={file.download_url}
          target="_blank"
          rel="noopener noreferrer"
          download={file.filename}
          title={`Download ${file.filename}`}
          aria-label={`Download ${file.filename}`}
          className="shrink-0 rounded p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-blue-600 dark:text-zinc-500 dark:hover:bg-zinc-800"
        >
          <Download className="h-3.5 w-3.5" aria-hidden />
        </a>
      )}
      {canDelete && (
        <button
          type="button"
          onClick={onDelete}
          disabled={deleting}
          title={`Remove ${file.filename}`}
          aria-label={`Remove ${file.filename}`}
          className="shrink-0 rounded p-1 text-neutral-400 transition-colors hover:bg-rose-50 hover:text-rose-600 disabled:cursor-wait dark:text-zinc-500 dark:hover:bg-rose-950/30"
        >
          {deleting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          )}
        </button>
      )}
    </div>
  )
}
